import { useLayoutEffect } from '@react-aria/utils';
import clsx from 'clsx';
import { useRef } from 'react';
import { Input as RACInput, type InputProps as RACInputProps } from 'react-aria-components';
import type { CommonProps } from '../../types.js';
import { textStyle } from '../../typography/utils.js';

interface InputProps extends CommonProps, Exclude<RACInputProps, 'className' | 'style'> {
  width?: 'fit-content' | 'full-width';
}

export function Input(props: InputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  // TODO: find a better way to extract common props, if a new prop is added to `CommonProps` this will break
  const {
    id,
    UNSAFE_className,
    UNSAFE_style,
    'data-cim-style-root': dataCimStyleRoot,

    width,

    ...inputProps
  } = props;

  // Field sizing based on input length for browsers that don't support `field-sizing: content`
  useLayoutEffect(() => {
    const input = inputRef.current;

    function setInputLengthStyle() {
      // Ensure input value changes are flushed before updating input width, especially in Safari
      requestAnimationFrame(() =>
        input?.style.setProperty('--cim-input-length', input?.value.length.toString() ?? '0'),
      );
    }

    if (input != null) {
      setInputLengthStyle();

      input.addEventListener('input', setInputLengthStyle);
      input.addEventListener('change', setInputLengthStyle);
    }

    return () => {
      input?.removeEventListener('input', setInputLengthStyle);
      input?.removeEventListener('change', setInputLengthStyle);
    };
  }, []);

  return (
    <div
      id={id}
      className={clsx('cim-input', UNSAFE_className)}
      style={UNSAFE_style}
      data-cim-style-root={dataCimStyleRoot}
    >
      <RACInput
        {...inputProps}
        ref={inputRef}
        className={clsx('cim-input-control', textStyle({ variant: 'body' }))}
        data-width={width}
      />
    </div>
  );
}
